import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBilling } from '../../contexts/BillingContext';
import { billingAPI } from '../../services/api';
import { COPY, planEndingLine } from '../../lib/billing';
import { T } from './tokens';

/**
 * Strips at the top of the main pane.
 *
 *   Payment failed (plan status past_due), warning colour:
 *     admins  "Your last payment didn't go through. Update your card" -> Portal
 *     members "Billing needs attention. Ask an admin."
 *   Plan ending: the apps about to pause (planEndingLine).
 *   Add email: "Add your email address to start your free trial" -> Settings,
 *     only for an account with no address on file at all. Sign-in is
 *     Google/GitHub only and the provider vouches for the address, so there
 *     is no "verify your email" step (2026-10-09); the free trial still needs
 *     an address (one trial per address, lib/freeGrant.js).
 */

const stripBase = 'mx-4 mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg px-4 py-2 text-sm min-h-[44px]';
const warnStyle: React.CSSProperties = { background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', color: T.warn };
const infoStyle: React.CSSProperties = { background: T.surface, border: `1px solid ${T.borderRegular}`, color: T.t1 };
const okStyle: React.CSSProperties = { background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)', color: T.success };

export const PaymentFailedStrip: React.FC<{ isAdmin: boolean; projectId?: string | null }> = ({ isAdmin, projectId }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await billingAPI.portal({ projectId, returnTo: window.location.pathname });
      window.location.assign(r.data.url);
    } catch {
      setError(COPY.portalError);
      setBusy(false);
    }
  };
  return (
    <div role="alert" data-testid="payment-failed-strip" className={stripBase} style={warnStyle}>
      {isAdmin ? (
        <>
          <span>{COPY.pastDueAdmin}</span>
          <button type="button" onClick={open} disabled={busy} className="underline underline-offset-2 min-h-[44px] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3264F8] rounded">
            {busy ? 'Opening…' : COPY.pastDueAdminCta}
          </button>
          {error && <span>{error}</span>}
        </>
      ) : (
        <span>{COPY.pastDueMember}</span>
      )}
    </div>
  );
};

/**
 * Decision 8: while the plan is canceling / past due / ended and published
 * apps would pause: "Your apps pause on Nov 5 unless your plan continues ·
 * Update billing" (Portal) for admins; members are pointed at an admin.
 */
export const PlanEndingStrip: React.FC<{ line: string; isAdmin: boolean; projectId?: string | null }> = ({ line, isAdmin, projectId }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await billingAPI.portal({ projectId, returnTo: window.location.pathname });
      window.location.assign(r.data.url);
    } catch {
      setError(COPY.portalError);
      setBusy(false);
    }
  };
  return (
    <div role="alert" data-testid="plan-ending-strip" className={stripBase} style={warnStyle}>
      <span>{line}</span>
      {isAdmin ? (
        <>
          <button type="button" onClick={open} disabled={busy} className="underline underline-offset-2 min-h-[44px] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3264F8] rounded">
            {busy ? 'Opening…' : COPY.planEndingCta}
          </button>
          {error && <span>{error}</span>}
        </>
      ) : (
        <span>{COPY.askAdminPlan}</span>
      )}
    </div>
  );
};

/** An account with no address on file: the free trial needs one (Settings). */
export const AddEmailStrip: React.FC = () => (
  <div data-testid="add-email-strip" className={stripBase} style={infoStyle}>
    <span>{COPY.addEmailStrip}</span>
    <Link to="/settings" className="underline underline-offset-2 min-h-[44px] inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3264F8] rounded" style={{ color: T.t2 }}>
      {COPY.addEmailCta}
    </Link>
  </div>
);

/** All strips, in priority order. Mounted once at the top of <main>. */
const AccountStrips: React.FC = () => {
  const { user } = useAuth() as any;
  const { balance, visible, scopeProjectId } = useBilling();

  if (!user) return null;

  const strips: React.ReactNode[] = [];
  if (visible && balance && balance.plan && balance.plan.status === 'past_due') {
    strips.push(<PaymentFailedStrip key="pastdue" isAdmin={balance.isAdmin} projectId={scopeProjectId} />);
  }
  // Past due already has its own strip above; this one covers canceling
  // and ended plans whose apps are about to pause (and past due too, when
  // the date is known: the two say different things).
  const ending = visible && balance ? planEndingLine(balance) : null;
  if (ending) {
    strips.push(<PlanEndingStrip key="ending" line={ending} isAdmin={balance!.isAdmin} projectId={scopeProjectId} />);
  }
  // No address at all (a GitHub account whose address GitHub did not hand
  // over). Never about verification: the sign-in provider vouches for it.
  if (visible && !(user.email && String(user.email).trim())) {
    strips.push(<AddEmailStrip key="add-email" />);
  }
  return strips.length > 0 ? <div data-testid="account-strips">{strips}</div> : null;
};

export default AccountStrips;
